import { useEffect, useState, useContext } from "react";
import * as _ from "lodash";

import "./UKMRVVoyages.scss";

import CustomTable from "../../../../../../common/customTable/CustomTable";
import { UKMRVVoyageColumnsData } from "../../../../../../constants/CustomTableHeaders";
import { useSelector } from "react-redux";
import { ClaimsContext } from "../../../../../../services/ClaimsService";

const UKMRVVoyagesNewVersion = ({ data, loading }) => {
	const { userHasClaim } = useContext(ClaimsContext);
	const [columnData, setColumnData] = useState([]);

	const user = useSelector((state) => state.userInfo.userData);

	const alwaysColumns = UKMRVVoyageColumnsData?.filter(
		(column) => !column.condition
	);
	const conditionalColumns = UKMRVVoyageColumnsData?.filter(
		(column) => column.condition && userHasClaim(column.condition)
	);

	const staticColumns = [
		...alwaysColumns.slice(0, 8),
		...conditionalColumns.filter(
			(item) => item.accessorKey === "calcTimeAtSea"
		),
		...alwaysColumns.slice(8, 10),
		...conditionalColumns.filter(
			(item) => item.accessorKey === "calcDistanceTravelled"
		),
		...alwaysColumns.slice(10),
	];

	useEffect(() => {
		if (
			data &&
			data[0]?.voyageConsumptions.some((fuel) =>
				["MDO", "MGO"].includes(fuel.fuelName)
			)
		) {
			data.forEach((entry) => {
				if (
					entry?.voyageConsumptions.every(
						(fuel) => fuel.fuelName !== "DieselGasOilCons"
					)
				) {
					entry.voyageConsumptions.unshift({
						fuelName: "DieselGasOilCons",
						consumption: entry.dieselGasOilCons,
					});
				}
			});
		}
	}, [data]);

	// method to filter dynamic columns based on non-zero values
	const getFilteredColumns = (data, columnType, columns) => {
		const nonZeroNames = new Set();

		// Collect non-zero fuel names based on the type (consumption, MNC, transport work)
		data.forEach((item) => {
			item[columnType].forEach((entry) => {
				if (
					columnType === "voyageConsumptions" &&
					entry.consumption !== 0 &&
					entry.consumption !== null &&
					entry.consumption !== undefined
				) {
					nonZeroNames.add(entry?.fuelName);
				}
				if (
					columnType === "voyageMncs" &&
					entry.mnc !== 0 &&
					entry.mnc !== null &&
					entry.mnc !== undefined
				) {
					nonZeroNames.add(entry?.fuelName);
				}
				if (
					columnType === "transportWorks" &&
					entry.value !== 0 &&
					entry.value !== null &&
					entry.value !== undefined
				) {
					nonZeroNames.add(entry?.transportWorkType);
				}
			});
		});

		// Filter columns based on non-zero values
		return columns.filter((column) => {
			const name = column.accessorKey.split("_")[1];
			return nonZeroNames.has(name);
		});
	};

	// method to filter static columns based on non-zero values
	const getFilteredStaticColumns = (objArray, staticColumns) => {
		const nonZeroHeaders = new Set();
		objArray?.forEach((obj) => {
			Object?.entries(obj)?.forEach(([key, value]) => {
				if (value !== 0 && value !== null && value !== undefined) {
					nonZeroHeaders.add(key);
				}
			});
		});
		return (
			staticColumns?.filter((column) =>
				nonZeroHeaders.has(column.accessorKey)
			) || []
		);
	};

	const GetPossibleKeys = (allData, dataType, keyName) => {
		return _.chain(allData).flatMap(dataType).map(keyName).uniq().value();
	};

	// Initialize dynamic columns for consumption, MNC, and transport work
	const getDynamicColumns = (data) => {
		const dynamicColumns = [];
		const dynamicMncColumns = [];

		if (Array.isArray(data[0]?.voyageConsumptions)) {
			const possibleKeys = GetPossibleKeys(
				data,
				"voyageConsumptions",
				"fuelName"
			);
			possibleKeys?.forEach((item) => {
				dynamicColumns.push({
					id: `voyageConsumptions_${item}`,
					accessorKey: `voyageConsumptions_${item}`,
					header: `${
						item === "DieselGasOilCons"
							? "Diesel/Gas Oil Cons. (MT)"
							: `${item} Cons. (MT)`
					}`,
					Cell: ({ row }) =>
						row.original.voyageConsumptions.find(
							(fuel) => fuel.fuelName === item
						)?.consumption || 0,
				});
			});
		}

		if (Array.isArray(data[0]?.voyageMncs)) {
			const possibleKeys = GetPossibleKeys(
				data,
				"voyageMncs",
				"fuelName"
			);
			possibleKeys?.forEach((item) => {
				dynamicMncColumns.push({
					id: `voyageMncs_${item}`,
					accessorKey: `voyageMncs_${item}`,
					header: `${item} MNC. (MT)`,
					Cell: ({ row }) =>
						row.original.voyageMncs.find(
							(fuel) => fuel.fuelName === item
						)?.mnc || 0,
				});
			});
		}

		// Return filtered dynamic columns based on data content
		return {
			dynamicColumns: getFilteredColumns(
				data,
				"voyageConsumptions",
				dynamicColumns
			),
			dynamicMncColumns: getFilteredColumns(
				data,
				"voyageMncs",
				dynamicMncColumns
			),
		};
	};

	useEffect(() => {
		if (loading) {
			setColumnData(staticColumns);
		} else {
			const { dynamicColumns, dynamicMncColumns } =
				getDynamicColumns(data);
			setColumnData([]);
			if (data && data.length > 0) {
				const dynamicEUMRVVoyageColumnsData =
					dynamicColumns || dynamicMncColumns
						? [
								...staticColumns.slice(0, 1),
								...getFilteredStaticColumns(
									data,
									staticColumns?.slice(1, 16)
								),
								...dynamicColumns,
								...dynamicMncColumns,
								...staticColumns?.slice(16, 17),
						  ]
						: getFilteredStaticColumns(data, staticColumns);

				setColumnData([...dynamicEUMRVVoyageColumnsData]);
			} else {
				setColumnData(staticColumns);
			}
		}
	}, [data, loading]);

	return (
		<>
			<div className="UKMRV-custom-table-container">
				<CustomTable
					columnsData={columnData}
					renderTableData={data}
					customProps={{
						isLoading: loading,
						enableSort: false,
						columnOrder: columnData.map(
							(column) => column.accessorKey
						),
						enableToptoolbar: false,
					}}
				/>
			</div>
		</>
	);
};

export default UKMRVVoyagesNewVersion;
